// Draws Alert.alert calls (see alert-api.js) as an in-app dialog, so the web
// supports the same button lists (cancel / destructive / several options) as
// iOS and Android. Mounted once in web/index.js.
import React, {useEffect, useState} from 'react';
import {subscribe} from './alert-api';
import {Pressable, StyleSheet, Text, View} from 'react-native-web';
import {colors, radii, spacing, typography} from '../../src/config/theme';

export const WebAlertHost = () => {
  const [queue, setQueue] = useState([]);

  useEffect(() => subscribe(alert => setQueue(q => [...q, alert])), []);

  const current = queue[0];
  if (!current) {
    return null;
  }

  const close = button => {
    setQueue(q => q.slice(1));
    button?.onPress?.();
  };
  const dismiss = () => {
    if (!current.cancelable) {
      return;
    }
    setQueue(q => q.slice(1));
    current.onDismiss?.();
  };
  const stacked = current.buttons.length > 2;

  return (
    <View style={styles.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={dismiss} />
      <View style={styles.dialog} accessibilityRole="alert">
        {!!current.title && <Text style={styles.title}>{current.title}</Text>}
        {!!current.message && (
          <Text style={styles.message}>{current.message}</Text>
        )}
        <View style={[styles.buttons, stacked && styles.buttonsStacked]}>
          {current.buttons.map((button, i) => (
            <Pressable
              key={`${button.text}-${i}`}
              onPress={() => close(button)}
              style={({hovered}) => [
                styles.button,
                hovered && styles.buttonHover,
              ]}>
              <Text
                style={[
                  styles.buttonText,
                  button.style === 'cancel' && styles.buttonTextCancel,
                  button.style === 'destructive' &&
                    styles.buttonTextDestructive,
                ]}>
                {button.text}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: colors.overlay,
  },
  dialog: {
    width: '100%',
    maxWidth: 320,
    padding: spacing.lg,
    borderRadius: radii.card,
    backgroundColor: colors.surface,
  },
  title: {...typography.headlineSm, color: colors.text, textAlign: 'center'},
  message: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.sm,
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: spacing.lg,
    gap: spacing.sm,
  },
  buttonsStacked: {flexDirection: 'column', alignItems: 'stretch'},
  button: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.sm,
    alignItems: 'center',
  },
  buttonHover: {backgroundColor: colors.surfaceMuted},
  buttonText: {...typography.labelLg, color: colors.primary},
  buttonTextCancel: {color: colors.textMuted},
  buttonTextDestructive: {color: colors.error},
});
